import { useState } from "react";
import type { CallLogItem } from "./CallLogsTab";
import MiniWaveformPlayer from "./MiniWaveformPlayer";

interface Props {
  calls?: CallLogItem[];
  onViewCall?: (call: CallLogItem) => void;
}

export default function RecordingsTab({ calls = [], onViewCall }: Props) {
  const recordedCalls = calls.filter((c) => c.hasRecording || c.recording_url);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | number | null>(null);

  if (recordedCalls.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 flex flex-col items-center justify-center py-20 text-center shadow-xs">
        <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
          <i className="ri-mic-line text-2xl text-indigo-500" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-1">No call recordings found</h3>
        <p className="text-xs text-gray-500 max-w-sm mb-5 leading-relaxed">
          Calls with audio attachments or recordings synced via Cloud Telephony (Exotel / Twilio / Asterisk) will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-xs">
      <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Recorded Calls ({recordedCalls.length})</h3>
          <p className="text-xs text-gray-500 mt-0.5">Listen to recorded customer conversations</p>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {recordedCalls.map((call) => (
          <div key={call.id} className="p-4 flex items-center justify-between hover:bg-gray-50/60 transition-colors">
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 font-bold flex items-center justify-center text-sm flex-shrink-0">
                {call.leadName?.slice(0, 2).toUpperCase() || "CL"}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{call.leadName}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  <span>{call.phone}</span>
                  <span>&bull;</span>
                  <span>{call.agent}</span>
                  <span>&bull;</span>
                  <span>{call.date}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 flex-shrink-0">
              <MiniWaveformPlayer
                durationSeconds={call.durationSeconds || 60}
                callId={Number(call.id) || 1}
                currentlyPlaying={Number(currentlyPlaying) || null}
                onPlay={(id) => setCurrentlyPlaying(id)}
                onPause={() => setCurrentlyPlaying(null)}
              />
              {onViewCall && (
                <button
                  onClick={() => onViewCall(call)}
                  className="px-2.5 py-1 text-xs border border-gray-200 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors cursor-pointer"
                >
                  View Details
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}